/**
 * WHOLESALE (Buy by the Dozen) — front-end behaviour.
 *
 * Loaded ONLY on wholesale product pages (by sections/main-product.liquid when
 * template.suffix == 'wholesale') and, in Phase 2, on cart surfaces.
 *
 * Design principles:
 *  - Never modify or replace theme JS. The theme's <input name="quantity">
 *    remains the single submitted quantity (in PIECES); this file only writes
 *    (dozens x 12) into it.
 *  - All money strings are precomputed in Liquid per variant (see
 *    snippets/wholesale-dozens-selector.liquid); JS only computes running
 *    totals, using the store's own money format string.
 *  - Variant-change syncing updates prices IMMEDIATELY on option click by
 *    matching selected options against the embedded variant map, then
 *    reconciles again when the theme publishes variantChange after its
 *    section fetch. This removes the slow "wait for network" lag.
 *
 * Docs: wholesale-dozen-implementation/IMPLEMENTATION-GUIDE.md
 */

(function () {
  'use strict';

  // Idempotence guard — this file can legitimately be included by more than
  // one surface on the same page (e.g. the cart drawer renders on every page
  // while the wholesale PDP or the cart page also loads it). Executing twice
  // would register a duplicate capture-phase cart listener (double dozens
  // conversion) and re-define the custom element (throws). Run once, ever.
  if (window.__wholesaleDozensInit) return;
  window.__wholesaleDozensInit = true;

  var MULTIPLIER_DEFAULT = 12;

  /* ------------------------------------------------------------------ *
   * Money formatting (totals only — per-variant strings come from Liquid)
   * ------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '{{amount}}';

    function addThousands(number, thousands, decimalSep, decimals) {
      var fixed = (number / 100).toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals > 0 ? whole + decimalSep + parts[1] : whole;
    }

    var match = formatString.match(placeholderRegex);
    var placeholder = match ? match[1] : 'amount';

    switch (placeholder) {
      case 'amount_no_decimals':
        value = addThousands(cents, ',', '.', 0);
        break;
      case 'amount_with_comma_separator':
        value = addThousands(cents, '.', ',', 2);
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = addThousands(cents, '.', ',', 0);
        break;
      case 'amount_with_apostrophe_separator':
        value = addThousands(cents, "'", '.', 2);
        break;
      case 'amount_with_currency':
      case 'amount':
      default:
        value = addThousands(cents, ',', '.', 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ------------------------------------------------------------------ *
   * Shared helpers for variant resolution
   * ------------------------------------------------------------------ */
  function readVariantMap(root) {
    var script = root.querySelector('[data-wholesale-variants]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('[wholesale] Could not parse variant map', e);
      return {};
    }
  }

  /**
   * Resolve the currently selected variant id from the product form / picker.
   * Order of preference:
   *  1. form input[name=id] (authoritative after theme updates it)
   *  2. option label match against the embedded variant map
   */
  function resolveVariantId(variants) {
    var idInput =
      document.querySelector('.product--wholesale form[data-type="add-to-cart-form"] input[name="id"]') ||
      document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]') ||
      document.querySelector('product-info form input[name="id"]');
    if (idInput && idInput.value) return String(idInput.value);

    var selected = [];
    document
      .querySelectorAll(
        'variant-selects select option[selected], variant-selects fieldset input:checked, variant-radios fieldset input:checked'
      )
      .forEach(function (el) {
        selected.push(el.value);
      });
    if (selected.length && variants) {
      var key = selected.join(' / ');
      for (var id in variants) {
        if (!Object.prototype.hasOwnProperty.call(variants, id)) continue;
        var opts = variants[id].options || [];
        if (opts.join(' / ') === key) return String(id);
      }
    }
    return null;
  }

  /**
   * Notify every registered listener that the active variant changed.
   * Used for both the immediate (option-click) path and the theme pub/sub path.
   */
  var variantListeners = [];
  function notifyVariantChange(variantId, variant) {
    if (!variantId) return;
    variantListeners.forEach(function (cb) {
      try {
        cb(String(variantId), variant || null);
      } catch (e) {
        console.error('[wholesale] variant listener error', e);
      }
    });
  }

  function onVariantChange(callback) {
    variantListeners.push(callback);
  }

  // Wire up listeners once (not once per custom-element instance)
  function wireVariantSources() {
    // 1) Immediate path: option selection inside the picker
    document.addEventListener(
      'change',
      function (event) {
        if (!event.target.closest('variant-selects, variant-radios, variant-picker')) return;

        // Try to resolve from selected options right away (no network)
        var anySelector = document.querySelector('wholesale-dozens');
        var variants = anySelector ? readVariantMap(anySelector) : {};
        var id = resolveVariantId(variants);
        if (id) notifyVariantChange(id, variants[id] || null);

        // Re-check shortly in case the theme updates the id input mid-flight
        window.setTimeout(function () {
          var id2 = resolveVariantId(variants);
          if (id2) notifyVariantChange(id2, variants[id2] || null);
        }, 40);
      },
      true
    );

    // 2) Theme pub/sub (authoritative, after section fetch)
    try {
      if (
        typeof window.subscribe === 'function' &&
        window.PUB_SUB_EVENTS &&
        window.PUB_SUB_EVENTS.variantChange
      ) {
        window.subscribe(window.PUB_SUB_EVENTS.variantChange, function (event) {
          var variant =
            (event && event.data && event.data.variant) ||
            (event && event.variant) ||
            null;
          if (variant && variant.id) {
            notifyVariantChange(String(variant.id), variant);
          } else {
            var anySelector = document.querySelector('wholesale-dozens');
            var variants = anySelector ? readVariantMap(anySelector) : {};
            var id = resolveVariantId(variants);
            if (id) notifyVariantChange(id, variants[id] || null);
          }
        });
      }
    } catch (e) {
      /* fall through */
    }

    // 3) Direct watch on the id input (covers updateVariantInputs change event)
    document.addEventListener(
      'change',
      function (event) {
        if (!event.target || event.target.name !== 'id') return;
        if (!event.target.closest('form[data-type="add-to-cart-form"], form[action="/cart/add"]')) return;
        if (event.target.value) notifyVariantChange(String(event.target.value), null);
      },
      true
    );
  }

  /* ------------------------------------------------------------------ *
   * <wholesale-dozens> — the dozens selector
   * ------------------------------------------------------------------ */
  var WholesaleDozens = { readVariantMap: readVariantMap };

  if (window.customElements && !customElements.get('wholesale-dozens')) {
    customElements.define(
      'wholesale-dozens',
      class extends HTMLElement {
        connectedCallback() {
          this.multiplier = parseInt(this.dataset.multiplier, 10) || MULTIPLIER_DEFAULT;
          this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
          this.input = this.querySelector('[data-wd-input]');
          this.targetInput = document.getElementById(this.dataset.targetInput);
          this.variants = WholesaleDozens.readVariantMap(this);
          this.currentVariantId = this.initialVariantId();

          this.bindEvents();
          this.applyDozens(this.getDozens()); // initial sync: 1 dozen -> 12 pieces

          var self = this;
          onVariantChange(function (variantId) {
            if (self.currentVariantId === variantId) {
              // Still refresh total in case dozens changed, but skip redundant work
              self.render();
              return;
            }
            self.currentVariantId = variantId;
            self.render();
          });
        }

        initialVariantId() {
          var id = resolveVariantId(this.variants);
          if (id) return id;
          var keys = Object.keys(this.variants);
          return keys.length ? keys[0] : null;
        }

        bindEvents() {
          var self = this;

          this.querySelectorAll('[data-wd-step]').forEach(function (button) {
            button.addEventListener('click', function () {
              var delta = parseInt(button.dataset.wdStep, 10);
              self.applyDozens(self.getDozens() + delta);
            });
          });

          this.querySelectorAll('[data-wd-preset]').forEach(function (button) {
            button.addEventListener('click', function () {
              self.applyDozens(parseInt(button.dataset.wdPreset, 10));
            });
          });

          this.input.addEventListener('change', function () {
            self.applyDozens(self.getDozens());
          });
          this.input.addEventListener('input', function () {
            // Live totals while typing, but do not fight partial input.
            if (self.input.value !== '') self.applyDozens(self.getDozens(), true);
          });
        }

        getDozens() {
          var value = parseInt(this.input.value, 10);
          if (isNaN(value) || value < 1) value = 1;
          return value;
        }

        /**
         * The single write path: normalises the dozens value, mirrors
         * (dozens x multiplier) PIECES into the theme's real quantity input,
         * and refreshes the summary UI.
         */
        applyDozens(dozens, soft) {
          if (isNaN(dozens) || dozens < 1) dozens = 1;
          if (!soft) this.input.value = dozens;

          if (this.targetInput) {
            // .value only — deliberately no 'change' event, so the theme does
            // not trigger its own quantity-rules re-fetch. Both Add to Cart
            // and dynamic checkout read the value at submit time.
            this.targetInput.value = dozens * this.multiplier;
          } else {
            console.error(
              '[wholesale] Target quantity input not found: #' + this.dataset.targetInput
            );
          }

          this.render(dozens);
        }

        render(dozens) {
          dozens = dozens || this.getDozens();
          var pieces = dozens * this.multiplier;
          var variant = this.variants[this.currentVariantId];

          // Preset highlighting
          this.querySelectorAll('[data-wd-preset]').forEach(function (button) {
            var active = parseInt(button.dataset.wdPreset, 10) === dozens;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-pressed', active ? 'true' : 'false');
          });

          // Summary line
          var dozensLabel = this.querySelector('[data-wd-dozens-label]');
          var piecesEl = this.querySelector('[data-wd-pieces]');
          var totalEl = this.querySelector('[data-wd-total]');
          if (dozensLabel) dozensLabel.textContent = dozens + (dozens === 1 ? ' dozen' : ' dozens');
          if (piecesEl) piecesEl.textContent = pieces;
          if (totalEl && variant && typeof variant.piece_cents === 'number') {
            totalEl.textContent = formatMoney(variant.piece_cents * pieces, this.moneyFormat);
          }

          // Availability state
          this.classList.toggle('wholesale-dozens--unavailable', !!variant && !variant.available);
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * Per-dozen price block sync (snippets/wholesale-price.liquid)
   * ------------------------------------------------------------------ */
  function initWholesalePrice() {
    var priceBlocks = document.querySelectorAll('[data-wholesale-price]');
    if (!priceBlocks.length) return;

    onVariantChange(function (variantId) {
      priceBlocks.forEach(function (block) {
        var selector = document.querySelector(
          'wholesale-dozens[data-section="' + block.dataset.section + '"]'
        );
        if (!selector) return;
        var variants = WholesaleDozens.readVariantMap(selector);
        var variant = variants[variantId];
        if (!variant) return;

        var priceEl = block.querySelector('[data-wd-dozen-price]');
        var pieceEl = block.querySelector('[data-wd-piece-price]');
        var compareWrap = block.querySelector('[data-wd-compare-wrap]');
        var compareEl = block.querySelector('[data-wd-compare]');

        if (priceEl) priceEl.textContent = variant.dozen_money;
        if (pieceEl) pieceEl.textContent = variant.piece_money;
        if (compareWrap && compareEl) {
          if (variant.compare_dozen_money) {
            compareEl.textContent = variant.compare_dozen_money;
            compareWrap.classList.remove('wholesale-hidden');
          } else {
            compareEl.textContent = '';
            compareWrap.classList.add('wholesale-hidden');
          }
        }
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart dozens module — DORMANT until Phase 2 cart Liquid ships.
   *
   * Contract: cart templates render, for wholesale lines only, a quantity
   * input carrying data-wd-cart-input and data-wd-multiplier="12", whose
   * DISPLAYED value/min/step are in DOZENS. This capture-phase listener
   * converts the value to PIECES before the theme's own cart JS (which
   * listens in the bubble phase) reads it, so the theme updates Shopify
   * with the correct piece quantity. After the theme re-renders the cart
   * from the server, the Liquid shows dozens again.
   * ------------------------------------------------------------------ */
  function initCartDozens() {
    document.addEventListener(
      'change',
      function (event) {
        var input = event.target;
        if (!input.matches || !input.matches('[data-wd-cart-input]')) return;
        if (input.dataset.wdConverted === '1') return; // re-entry guard

        var multiplier = parseInt(input.dataset.wdMultiplier, 10) || MULTIPLIER_DEFAULT;
        var dozens = parseInt(input.value, 10);
        if (isNaN(dozens) || dozens < 0) dozens = 0;

        input.dataset.wdConverted = '1';
        input.value = dozens * multiplier;
        window.requestAnimationFrame(function () {
          delete input.dataset.wdConverted;
        });
      },
      true // capture phase: runs BEFORE the theme's bubble-phase handler
    );
  }

  /* ------------------------------------------------------------------ */
  function init() {
    wireVariantSources();
    initWholesalePrice();
    initCartDozens();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();